import React, { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useMutation } from 'react-query';
import styles from './LibraryAccessForm.module.scss';
import TextField from '../../components/TextField/TextField';
import { useTranslation } from 'react-i18next';
import env from '@jwp/ott-common/src/env';
import Button from '../Button/Button';
import { useNavigate, useLocation } from 'react-router';
import Cookies from 'js-cookie';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { signupUser, type UserData } from '@jwp/ott-common/src/controllers/graphql';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import FormFeedback from '../FormFeedback/FormFeedback';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { getLocalStorageValue } from '@jwp/ott-common/src/constants';
interface LibraryValue {
  id?: string;
  value?: string;
}

interface FormData {
  libraryId: LibraryValue;
  libraryNumber: string;
  pin: string[];
  dob: string;
  userIpAddress: string;
  checkAgeFormat: boolean;
  skipped: boolean;
}

interface SelectedOptions {
  dob: string;
  pin: string;
}

interface Restriction {
  name: string;
  type: string;
}

type Props = {
  onCloseButtonClick: () => void;
};

const LibraryAccess = ({ onCloseButtonClick }: Props) => {
  const [formData, setFormData] = useState<FormData>({
    libraryId: { id: '', value: '' },
    libraryNumber: '',
    pin: Array(4).fill(''),
    dob: '',
    checkAgeFormat: true,
    userIpAddress: '',
    skipped: false,
  });

  const { t, i18n } = useTranslation('account');
  const [selectedOption, setSelectedOption] = useState<SelectedOptions>({ dob: 'dob', pin: 'pin' });
  const [errors, setErrors] = useState('');
  const [inputError, setInputError] = useState({
    selectedlibrary: '',
    Library_Number: '',
    dob: '',
    pin: '',
  });
  // const libraryNumberRegex = /^\S+$/;
  const libraryNumberRegex = /^[a-zA-Z0-9]*$/;

  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfigStore((s) => s.config);
  const {
    assets: { banner },
  } = config;
  const htmlLang = i18n.language !== env.APP_DEFAULT_LANGUAGE ? env.APP_DEFAULT_LANGUAGE : undefined;
  const defaultLanguage = config?.custom?.defaultLanguage;
  const LocalStorageValue = getLocalStorageValue();

  // get user token
  let token = '';
  try {
    const storedToken = localStorage.getItem('inplayer_token');
    if (storedToken) {
      token = JSON.parse(storedToken).token;
    }
  } catch (error) {
    console.error('Error parsing token from localStorage:', error);
  }

  const mutation = useMutation(signupUser, {
    onError: (error) => {
      console.error('Mutation error:', error);
      setErrors((error as Error).message);
    },
    onSuccess: (data) => {
      if (data) {
        Cookies.set('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }), {
          domain: '.bibliotele.com',
          expires: 7,
        });

        localStorage.setItem('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }));
        sessionStorage.removeItem('isSignInProgress');
        getModule(AccountController).getAccount();
        navigate(modalURLFromLocation(location, location?.state?.isComeFrom === 'login' && location?.state?.isComeFromEditPage ? null : 'welcome'));
      }
    },
  });

  const { isLoading: isMutationLoading } = mutation;

  const handlePinChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    const newPin = [...formData.pin];
    //This condition is for validation purposes and only applies when the value is a number.
    if (/^\d*$/.test(value)) {
      newPin[index] = value;
      setFormData({ ...formData, pin: newPin });
      setErrors('');
      // Auto-focus the next or previous input field based on the input value
      if (value) {
        if (index < 3) {
          const nextInput = document.getElementById(`pin-${index + 1}`) as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
          }
        }
      } else {
        if (index > 0) {
          const prevInput = document.getElementById(`pin-${index - 1}`) as HTMLInputElement;
          if (prevInput) {
            prevInput.focus();
          }
        }
      }
    }
  };

  const handleSignup = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    let hasError = false;
    const errorState = { ...inputError };

    // Validate PIN
    if (selectedOption.pin === 'pin') {
      if (formData.pin.join('').length !== 4) {
        hasError = true;
        errorState.pin = t('Library_Access.Library_valid_screen.pin_error');
      } else {
        errorState.pin = '';
      }
    }

    // Validate Library Number
    if (!formData?.libraryNumber?.trim()) {
      hasError = true;
      errorState.Library_Number = t('Library_Access.Library_valid_screen.card_number_required');
    } else if (formData?.libraryNumber?.length >= 30) {
      hasError = true;
      errorState.Library_Number = 'Library card number must be less than 30 characters long';
    } else if (!libraryNumberRegex?.test(formData?.libraryNumber)) {
      hasError = true;
      errorState.Library_Number = t('Library_Access.Library_valid_screen.library_card_error');
    } else {
      errorState.Library_Number = '';
    }

    setInputError(errorState);

    if (hasError) {
      return;
    }

    // Prepare user data
    const userData = {
      userToken: token,
      ...(formData.libraryId.id && { libraryId: formData.libraryId.id }),
      ...(formData.libraryNumber && { libraryNumber: formData.libraryNumber }),
      ...(formData.pin.join('') && selectedOption.pin && { pinNumber: formData.pin.join('') }),
      ...(formData.userIpAddress && { userIpAddress: formData.userIpAddress }),
      isAdult: location?.state?.libraryType === 'academic' ? true : formData.checkAgeFormat,
      skipped: formData.skipped,
      languageCode: LocalStorageValue ?? defaultLanguage,
    };

    mutation.mutate(userData as UserData);
  };

  const pinValue = formData.pin.join('');

  useEffect(() => {
    if (pinValue.length === 4 && inputError.pin) {
      setInputError((prevState) => ({ ...prevState, pin: '' }));
    }
  }, [pinValue, inputError.pin]);

  const onClickGoBack = () => {
    if (location?.state?.isComeFromEditPage) onCloseButtonClick();
    else {
      navigate(-1);
    }
  };
  const { libraryId, libraryName: libaryNum, libraryParent } = location?.state ?? {};

  const onClickSkip = () => {
    if (token) {
      if (formData.libraryId.id) {
        const userData = {
          userToken: token,
          libraryId: formData.libraryId.id,
          skipped: true,
          isAdult: true,
          languageCode: LocalStorageValue ?? defaultLanguage,
        };
        mutation.mutate(userData as UserData);
      } else setErrors(t('Library_Access.Library_valid_screen.select_your_library'));
    }
  };

  useEffect(() => {
    if (libraryId && libaryNum && libraryParent) {
      setFormData({ ...formData, libraryId: { id: libraryId, value: libaryNum } });
    }
    const newSelectedOption: SelectedOptions = { dob: '', pin: '' };
    libraryParent?.forEach(({ type }: Restriction) => {
      if (type === 'pin') {
        newSelectedOption.pin = type;
      } else if (type === 'dob') {
        newSelectedOption.dob = type;
      }
    });
    setSelectedOption(newSelectedOption);
  }, []); //eslint-disable-line

  useEffect(() => {
    if (errors) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }, [errors]);

  return (
    <div>
      <section className={styles.parent_section}>
        {banner && <img src={banner} alt="banner" className={styles.banner_img} />}
        {!location?.state?.isComeFromEditPage && (
          <p className={styles.skip_btn} onClick={onClickSkip}>
            {t('Library_Access.Library_valid_screen.skip_btn')}
          </p>
        )}
      </section>
      <form className={styles['form-style']}>
        <div ref={ref}>
          {errors ? (
            <FormFeedback variant="error" visible={!!errors}>
              {errors}
            </FormFeedback>
          ) : null}
        </div>

        <h2 className={styles.title}> {t('Library_Access.Library_valid_screen.header_text')}</h2>
        <div className={styles['library-access']}>
          <div className={styles['form-group']}>
            <TextField
              name="libraryNumber"
              label={t('Library_Access.Library_valid_screen.Library_Number')}
              value={formData.libraryNumber}
              // onChange={(e) => setFormData({ ...formData, libraryNumber: e.target.value.replace(/^\s+|\s+$/gm, '') })}
              onChange={(e) => {
                const value = e.target.value;
                if (libraryNumberRegex.test(value)) {
                  setFormData({ ...formData, libraryNumber: value?.replace(/^\s+|\s+$/gm, '') });
                  setInputError((prev) => ({ ...prev, Library_Number: '' }));
                }
              }}
              autoComplete="off"
              lang={htmlLang}
              type="text"
              required
              placeholder={t('Library_Access.Library_valid_screen.Enter_library_number')}
              error={!!inputError.Library_Number}
              helperText={inputError.Library_Number}
              className={styles['library_number']}
            />
          </div>
          {selectedOption.pin === 'pin' && (
            <div className={styles['form-group']}>
              <label>{t('Library_Access.Library_valid_screen.Pin_Number')}</label>
              <div className={styles['pin-input']}>
                {formData.pin.map((pin, index) => (
                  <input
                    key={index}
                    id={`pin-${index}`}
                    type="text"
                    maxLength={1}
                    value={pin}
                    onChange={(e) => handlePinChange(e, index)}
                    className={styles['pin-box']}
                    placeholder="-"
                    style={inputError.pin ? { border: '1px solid red' } : { border: 'rgba(255, 255, 255, 0.34)' }}
                  />
                ))}
              </div>
              {inputError.pin && <p className={styles['pin-error']}>{inputError.pin}</p>}
            </div>
          )}
          {location?.state?.libraryType !== 'academic' && (
            <div className={styles['form-group']}>
              <label className={styles['checkbox-lable-style']}>
                <input
                  type="checkbox"
                  checked={formData.checkAgeFormat}
                  onChange={(e) => setFormData({ ...formData, checkAgeFormat: e.target.checked })}
                  className={styles['checkbox-input']}
                />
                {t('Library_Access.Library_valid_screen.check_box_message')}
              </label>
            </div>
          )}
        </div>
        <div className={styles['footer-button']}>
          <Button
            variant="text"
            label={
              location?.state?.isComeFromEditPage ? t('Library_Access.Library_valid_screen.Cancel_btn') : t('Library_Access.Library_valid_screen.Back_btn')
            }
            size="large"
            fullWidth
            className={styles['skip-btn']}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onClickGoBack();
            }}
          />
          <Button
            type="submit"
            label={
              location?.state?.isComeFromEditPage ? t('Library_Access.Library_valid_screen.Update_btn') : t('Library_Access.Library_valid_screen.Submit_btn')
            }
            color="primary"
            size="large"
            fullWidth
            className={styles['submit-btn']}
            onClick={(e) => handleSignup(e)}
          />
        </div>
        {isMutationLoading && <LoadingOverlay inline />}
      </form>
    </div>
  );
};

export default LibraryAccess;
